/**
 * Format a numeric value as currency with fixed 2-decimal precision.
 *
 * @param value - amount; null/undefined becomes em-dash placeholder
 * @param currency - ISO-4217 currency code (default GBP)
 * @returns localized money string, e.g. "£1,234.56" / "$0.00" / "—"
 */
export function formatMoney(value: number | null | undefined, currency: string = 'GBP'): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—'; // em dash
    }
    try {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        // Fall back if currency code is invalid.
        return `${currency} ${value.toFixed(2)}`;
    }
}

/**
 * Format the difference between now and the given Date as a relative phrase
 * ("just now", "5 seconds ago", "2 minutes ago", "3 hours ago").
 */
export function formatRelativeTime(target: Date | null | undefined, now: Date = new Date()): string {
    if (!target) return 'never';
    const diffSeconds = Math.round((target.getTime() - now.getTime()) / 1000);
    const absSeconds = Math.abs(diffSeconds);

    if (absSeconds < 5) return 'just now';
    try {
        const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
        if (absSeconds < 60) return rtf.format(diffSeconds, 'second');
        if (absSeconds < 3600) return rtf.format(Math.round(diffSeconds / 60), 'minute');
        if (absSeconds < 86400) return rtf.format(Math.round(diffSeconds / 3600), 'hour');
        return rtf.format(Math.round(diffSeconds / 86400), 'day');
    } catch {
        return target.toLocaleString();
    }
}
